// packages import
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select
} from '@mui/material'
import { FC, useContext } from 'react'
import { Controller, FormProvider, SubmitHandler, useForm } from 'react-hook-form'
// component import
import { Alert } from '../common/Alert'
import { CustomModal } from '../common/CustomModal'
import { CustomController } from '../common/CustomController'

// others
import { ACCOUNT_CATEGORY, CANCEL } from '../../constants'
import { AccountType, CreateAccountInput, useCreateAccountMutation, useUpdateAccountMutation } from '../../../generated'
import { addNewAccountValidationSchema } from '../../validationSchema'
import { AddNewAccountProps, ErrorException } from '../../interfaceTypes'
import { ActionType } from '../../context/AppContextReducer'
import { AppContext } from '../../context'
import { ApolloError } from '@apollo/client'
import CheckboxController from '../common/CheckboxController'

export const AddNewAccount: FC<AddNewAccountProps> = ({ title = 'Add New', defaultValues, refetch }) => {
  const { isOpen: isNewAccountOpen, dispatch: appDispatch } = useContext(AppContext)

  const methods = useForm<CreateAccountInput>({
    mode: 'all',

    resolver: yupResolver(addNewAccountValidationSchema),
    defaultValues: {
      type: defaultValues?.type ?? AccountType.Operating,
      name: defaultValues?.name ?? '',
      accountNo: defaultValues?.accountNo ?? '',
      category: defaultValues?.category ?? undefined,
      sortCode: defaultValues?.sortCode ?? '',
      institution: defaultValues?.institution ?? '',
      description: defaultValues?.description ?? '',
      openingBalance: defaultValues?.openingBalance ?? null,
      routingNo: defaultValues?.routingNo ?? '',
      isBankAccount: defaultValues?.isBankAccount ?? false
    }
  })

  const {
    handleSubmit,
    control,
    formState: { errors },
    reset,
    setValue,
    clearErrors
  } = methods

  const handleMutationError = (error: ApolloError) => {
    const { graphQLErrors } = error

    if (graphQLErrors) {
      graphQLErrors.map(
        // eslint-disable-next-line
        ({ extensions }) => {
          if (extensions) {
            const { exception } = extensions
            if (exception) {
              const { response } = exception as ErrorException
              const { response: errorResponse } = response || {}
              if (errorResponse) {
                const { error } = errorResponse
                Alert.error(error)
              } else {
                Alert.error('Something went wrong')
              }
            }
          }
        }
      )
    }
  }

  const handleMutationSuccess = (
    data: any,
    successMessage: string,
    closeCallback: () => void,
    refetchCallback?: () => void
  ) => {
    const { response, account } = data?.createAccount || data?.updateAccount || {}

    if (response?.status === 200 && account) {
      Alert.success(successMessage)
      closeCallback()
      refetchCallback?.()
    }
  }

  const [createAccountMutation, { loading: createAccountLoader }] = useCreateAccountMutation({
    onError: handleMutationError,
    onCompleted(data) {
      handleMutationSuccess(data, 'Account Created Successfully', handleClose, refetch)
    }
  })

  const [updateAccountMutation, { loading: updateAccountLoader }] = useUpdateAccountMutation({
    onError: handleMutationError,
    onCompleted(data) {
      handleMutationSuccess(data, 'Account Updated Successfully', handleClose, refetch)
    }
  })

  const handleClose = () => {
    reset()
    clearErrors()
    if (isNewAccountOpen) {
      appDispatch({ type: ActionType.SET_IS_OPEN, isOpen: !isNewAccountOpen })
    }
  }

  const onSubmit: SubmitHandler<CreateAccountInput> = async (data: CreateAccountInput) => {
    const {
      type,
      name,
      category,
      sortCode,
      institution,
      accountNo,
      openingBalance,
      description,
      routingNo,
      isBankAccount
    } = data

    if (!type || !name || !category || !sortCode) {
      Alert.error('Please fill all the fields')
      return
    }

    const commonPayload = {
      name,
      accountNo,
      sortCode,
      institution,
      openingBalance,
      description,
      routingNo,
      isBankAccount
    }

    if (defaultValues?.id) {
      await updateAccountMutation({
        variables: {
          updateAccountInput: {
            id: defaultValues?.id,
            ...commonPayload
          }
        }
      })
      return
    }

    await createAccountMutation({
      variables: {
        createAccountInput: {
          ...commonPayload,
          type,
          category
        }
      }
    })
  }

  return (
    <CustomModal title={`${title} Account`} isOpen={isNewAccountOpen} handleClose={handleClose}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} rowSpacing={0.5}>
            <Grid item md={3} sm={12} xs={12}>
              <FormControlLabel
                label="Trust Account"
                control={
                  <Controller
                    name="type"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <Checkbox
                        checked={value === AccountType.Trust}
                        disabled={!!defaultValues?.id}
                        onChange={e => {
                          onChange(e)
                          setValue('type', e.target.checked ? AccountType.Trust : AccountType.Operating)
                        }}
                      />
                    )}
                  />
                }
              />
            </Grid>

            <Grid item md={3} sm={12} xs={12}>
              <CheckboxController label={'Bank Account'} name="isBankAccount" />
            </Grid>

            <Grid item md={12} sm={12} xs={12}>
              <FormControl variant="outlined" fullWidth sx={{ marginTop: 1 }}>
                <InputLabel id="category">Account Type</InputLabel>

                <Controller
                  name="category"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      labelId="category"
                      label="Account Type"
                      fullWidth
                      disabled={!!defaultValues?.id}
                      MenuProps={{ style: { maxHeight: 300 } }}
                      error={!!errors.category}
                    >
                      {ACCOUNT_CATEGORY?.map((item, index) => {
                        return (
                          <MenuItem value={item.value} key={`${item.value}-${index}`}>
                            {item.label}
                          </MenuItem>
                        )
                      })}
                    </Select>
                  )}
                />
                {!!errors.category && (
                  <FormHelperText sx={{ color: theme => theme.palette.error.main }}>
                    {errors.category?.message}
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            <Grid item md={12} sm={12} xs={12}>
              <CustomController controllerName="name" controllerLabel="Account Name" fieldType="text" />
            </Grid>

            <Grid item md={12} sm={12} xs={12}>
              <CustomController controllerName="accountNo" controllerLabel="Account Number" fieldType="text" />
            </Grid>

            <Grid item md={12} sm={12} xs={12}>
              <CustomController controllerName="routingNo" controllerLabel="Routing Number" fieldType="text" />
            </Grid>

            <Grid item md={12} sm={12} xs={12}>
              <CustomController controllerName="sortCode" controllerLabel="Sort Code" fieldType="text" />
            </Grid>

            {!defaultValues?.id && (
              <Grid item md={12} sm={12} xs={12}>
                <CustomController
                  controllerName="openingBalance"
                  controllerLabel="Opening Balance"
                  fieldType="number"
                  dollarAdornment
                />
              </Grid>
            )}

            <Grid item md={12} sm={12} xs={12}>
              <CustomController controllerName="institution" controllerLabel="Institution" fieldType="text" />
            </Grid>

            <Grid item md={12} sm={12} xs={12}>
              <CustomController
                controllerName="description"
                controllerLabel="Description"
                fieldType="text"
                isMultiLine={true}
                rowsLength={4}
              />
            </Grid>
          </Grid>

          <DialogActions sx={{ padding: '30px 0 0' }}>
            <Button onClick={handleClose} variant="outlined" aria-label={CANCEL}>
              {CANCEL}
            </Button>

            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={createAccountLoader || updateAccountLoader}
              aria-label="add new client"
              endIcon={(createAccountLoader || updateAccountLoader) && <CircularProgress size={20} color="inherit" />}
            >
              Save
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </CustomModal>
  )
}
